import { Auth } from 'aws-amplify';
import Cookies from 'js-cookie';

import { isRecord, isString } from '../helpers/type_guards';
import { getCookieDomain, getSecureCookie } from './cookie_handler';
import { useRootStore } from '../stores/root';

// export const tokenUsernameKey = 'tokenUsername';
// export const tokenKeyKey = 'tokenKey';
// export const refreshTokenKey = 'refreshToken';

// New per-app version cookie keys
/**
 * The keys for providing and consuming login information from the v2 and v3
 * applications. These keys are used to store the username, token key (what's used
 * in localStorage), and the refresh token. The prefix of the keys, v2 & v3, are
 * based upon the SENDER of the cookie. e.g. MyQ v2 will set a cookie starting with
 * v2. This app will set v2 cookies, and look out for v3 cookies.
 */
export const v2TokenUsernameKey = 'v2TokenUsername';
export const v2TokenKeyKey = 'v2TokenKey';
export const v2RefreshTokenKey = 'v2RefreshToken';
export const v2AssumedOrgIdKey = 'v2AssumedOrgId';
export const v2AssumedUserIdKey = 'v2AssumedUserId';

export const v3TokenUsernameKey = 'v3TokenUsername';
export const v3TokenKeyKey = 'v3TokenKey';
export const v3RefreshTokenKey = 'v3RefreshToken';
export const v3AssumedOrgIdKey = 'v3AssumedOrgId';
export const v3AssumedUserIdKey = 'v3AssumedUserId';

// Old app-less version cookies
export const tokenUsernameKey = 'tokenUsername';
export const tokenKeyKey = 'tokenKey';
export const refreshTokenKey = 'refreshToken';
export const assumedOrgIdKeyA = 'assumedOrgId';
export const assumedOrgIdKeyB = 'assumedOrgID';
export const assumedUserIdKey = 'assumedUserID';

// Function to parse and extract data from a JWT. Doesn't use any libraries
export function extractJWTData(input: string): Record<string, unknown> {
  const base64TokenData = input.split('.')[1];
  const base64 = base64TokenData.replace(/-/g, '+').replace(/_/g, '/');
  const tokenData = JSON.parse(atob(base64));

  if (!isRecord(tokenData)) {
    console.error('Validation Error tokenData is not an object');
    return {};
  }

  return tokenData;
}

export function constructCognitoStorageKey(
  accessToken: Record<string, unknown>,
  idToken: Record<string, unknown>,
): { cognitoPrefix: string; userPrefix: string } {
  const cognitoPrefix = `CognitoIdentityServiceProvider.${accessToken.client_id}.`;
  const userPrefix = `${cognitoPrefix}${idToken.email}.`;

  return { cognitoPrefix, userPrefix };
}

export async function retrieveTokensAndSetCookies() {
  const currentSession = await Auth.currentSession();
  const cognitoAccessToken = currentSession.getAccessToken().getJwtToken();
  const cognitoIdToken = currentSession.getIdToken().getJwtToken();
  const cognitoRefreshToken = currentSession.getRefreshToken().getToken();

  const accessToken = extractJWTData(cognitoAccessToken);
  const idToken = extractJWTData(cognitoIdToken);

  const email = idToken.email;

  if (!isString(email)) {
    throw new Error('Username/Email is not a string');
  }

  const { userPrefix } = constructCognitoStorageKey(accessToken, idToken);

  const domain = getCookieDomain();
  const secure = getSecureCookie();

  // New Tokens being set
  Cookies.set(v3TokenUsernameKey, email, {
    domain,
    secure,
  });
  Cookies.set(v3TokenKeyKey, userPrefix, {
    domain,
    secure,
  });
  Cookies.set(v3RefreshTokenKey, cognitoRefreshToken, {
    domain,
    secure,
  });

  // Old Tokens being set
  Cookies.set(tokenUsernameKey, email, {
    domain,
    secure,
  });
  Cookies.set(tokenKeyKey, userPrefix, {
    domain,
    secure,
  });
  Cookies.set(refreshTokenKey, cognitoRefreshToken, {
    domain,
    secure,
  });

  const store = useRootStore();

  if (store.assumingUser) {
    if (store.orgId && store.userId) {
      // console.log('setting cookie for assume user', store.orgId, store.userId);
      Cookies.set(assumedOrgIdKeyB, store.orgId, {
        domain,
        secure,
      });
      Cookies.set(v3AssumedOrgIdKey, store.orgId, {
        domain,
        secure,
      });

      Cookies.set(assumedUserIdKey, store.userId, {
        domain,
        secure,
      });
    }
  } else {
    if (store.orgId) {
      // console.log('setting cookie change client', store.orgId);
      Cookies.set(assumedOrgIdKeyB, store.orgId, {
        domain,
        secure,
      });
    }
  }
}

function unsetV2TokenUsernameCookie() {
  const domain = getCookieDomain();

  Cookies.remove(v2TokenUsernameKey, { domain });
  Cookies.remove(tokenUsernameKey, { domain });
}
function unsetV2TokenKeyCookie() {
  const domain = getCookieDomain();

  Cookies.remove(v2TokenKeyKey, { domain });
  Cookies.remove(tokenKeyKey, { domain });
}
function unsetV2RefreshTokenCookie() {
  const domain = getCookieDomain();

  Cookies.remove(v2RefreshTokenKey, { domain });
  Cookies.remove(refreshTokenKey, { domain });
}

function unsetV3TokenUserNameCookie() {
  const domain = getCookieDomain();

  Cookies.remove(v3TokenUsernameKey, { domain });
  Cookies.remove(tokenUsernameKey, { domain });
}
function unsetV3TokenKeyCookie() {
  const domain = getCookieDomain();

  Cookies.remove(v3TokenKeyKey, { domain });
  Cookies.remove(tokenKeyKey, { domain });
}
function unsetV3RefreshTokenCookie() {
  const domain = getCookieDomain();

  Cookies.remove(v3RefreshTokenKey, { domain });
  Cookies.remove(refreshTokenKey, { domain });
}

export function getTokenUsernameFromCookie() {
  const oldVal = Cookies.get(tokenUsernameKey);
  const newVal = Cookies.get(v2TokenUsernameKey);

  unsetV2TokenUsernameCookie();

  return newVal ?? oldVal;
}

export function getTokenKeyFromCookie() {
  const oldVal = Cookies.get(tokenKeyKey);
  const newVal = Cookies.get(v2TokenKeyKey);

  unsetV2TokenKeyCookie();

  return newVal ?? oldVal;
}

export function getRefreshTokenFromCookie() {
  const oldVal = Cookies.get(refreshTokenKey);
  const newVal = Cookies.get(v2RefreshTokenKey);

  unsetV2RefreshTokenCookie();

  return newVal ?? oldVal;
}

export function unsetAuthTokenCookies() {
  unsetV2TokenUsernameCookie();
  unsetV2TokenKeyCookie();
  unsetV2RefreshTokenCookie();

  unsetV3TokenUserNameCookie();
  unsetV3TokenKeyCookie();
  unsetV3RefreshTokenCookie();
  // Cookies.remove(tokenUsernameKey, { domain: '.myqsrsoft.com' });
  // Cookies.remove(tokenKeyKey, { domain: '.myqsrsoft.com' });
  // Cookies.remove(refreshTokenKey, { domain: '.myqsrsoft.com' });

  // Cookies.remove(tokenUsernameKey, { domain: 'localhost' });
  // Cookies.remove(tokenKeyKey, { domain: 'localhost' });
  // Cookies.remove(refreshTokenKey, { domain: 'localhost' });
}

function unsetAssumedUserCookies() {
  Cookies.remove(assumedUserIdKey, { domain: getCookieDomain() });
  Cookies.remove(v2AssumedUserIdKey, { domain: getCookieDomain() });
}
function unsetAssumedOrgCookies() {
  const domain = getCookieDomain();

  Cookies.remove(assumedOrgIdKeyA, { domain });
  Cookies.remove(assumedOrgIdKeyB, { domain });
  Cookies.remove(v2AssumedOrgIdKey, { domain });
}

// First we get the v3 cookie. If it doesn't exist, we get the original
// "legacy" cookies.
export function getAssumedOrgIdCookie() {
  const assumedOrgIDRaw =
    Cookies.get(v2AssumedOrgIdKey) ??
    (Cookies.get(assumedOrgIdKeyA) || Cookies.get(assumedOrgIdKeyB));

  unsetAssumedOrgCookies();

  return assumedOrgIDRaw;
}

// First we get the v3 cookie. If it doesn't exist, we get the original
// "legacy" cookies.
export function getAssumedUserIdCookie() {
  const assumedUserCookie =
    Cookies.get(v2AssumedUserIdKey) ?? Cookies.get(assumedUserIdKey);

  unsetAssumedUserCookies();

  return assumedUserCookie;
}
